import type * as BaseDataResponse from './types/api/base-data-response'
import type * as LoadBudget from './types/api/load-budget-response'

import { evaluateExpression } from '@app/application/report/forecast/expression-evaluator'
import { FormulaCalculator } from '@app/application/report/forecast/formula-calculator'

type BaseNode =
  | BaseDataResponse.Group
  | BaseDataResponse.Account
  | BaseDataResponse.Formula
  | BaseDataResponse.Spacer
  | BaseDataResponse.DataLine

type GroupNode = BaseDataResponse.Group & {
  sign: number
  parent: GroupNode | null
  value: number
  displayValue: number
  adjustedDisplayValue: number
  adjustmentPercentage: number | null
  adjustmentExpression: string | null
  isAdjustable: boolean
  children: ForecastNode[]
}
type AccountNode = Omit<BaseDataResponse.Account, 'value'> & {
  value: number
  displayValue: number
  adjustedValue: number
  adjustedDisplayValue: number
  parent: GroupNode
  adjustmentPercentage: number | null
  adjustmentExpression: string | null
  isAdjustable: true
}
type FormulaNode = BaseDataResponse.Formula & {
  value: number | null
  adjustedValue: number | null
  adjustmentPercentage: number | null
  isAdjustable: false
}
type SpacerNode = BaseDataResponse.Spacer & { isAdjustable: false }
type DataLineNode = BaseDataResponse.DataLine & { isAdjustable: false }

type ForecastNode =
  | GroupNode
  | AccountNode
  | FormulaNode
  | SpacerNode
  | DataLineNode

export type { ForecastNode, GroupNode, AccountNode, FormulaNode }

export class Forecast {
  private readonly baseData: BaseDataResponse.Data
  private readonly groupNodesById = new Map<number, GroupNode>()
  private readonly accountNodesById = new Map<number, AccountNode>()
  private readonly language: string
  readonly rootNodes: ForecastNode[]

  constructor(baseData: BaseDataResponse.Data, language: string) {
    this.baseData = baseData
    this.language = language
    this.rootNodes = this.constructBaseStructure()
    this.calculateValues()
    this.storeInitialValues()
    this.calculateAdjustmentPercentages()
  }

  private constructBaseStructure(): ForecastNode[] {
    return this.baseData
      .filter((element) => !element.groupId)
      .map((node) => this.buildNode(node))
  }

  private buildNode(node: BaseNode, parent?: GroupNode): ForecastNode {
    if (node.type === 'Account') {
      const displayValue = Number.parseFloat(node.value) * parent!.sign

      const accountNode: AccountNode = {
        ...node,
        value: Number.parseFloat(node.value),
        displayValue,
        adjustedValue: Number.parseFloat(node.value),
        adjustedDisplayValue: displayValue,
        parent: parent!,
        adjustmentPercentage: null,
        adjustmentExpression: null,
        isAdjustable: true,
      }

      this.accountNodesById.set(accountNode.id, accountNode)

      return accountNode
    }

    if (node.type !== 'Group') {
      if (node.type === 'Value') {
        return {
          ...node,
          value: null,
          adjustedValue: null,
          adjustmentPercentage: null,
          isAdjustable: false,
        }
      }

      return { ...node, isAdjustable: false }
    }

    const children = this.baseData.filter(
      (element) => element.groupId === node.id
    )

    const sign = (parent?.sign ?? 1) * (node.switchSign ? -1 : 1)

    const groupNode: GroupNode = {
      ...node,
      sign,
      parent: parent ?? null,
      value: 0,
      displayValue: 0,
      adjustedDisplayValue: 0,
      adjustmentPercentage: null,
      adjustmentExpression: null,
      isAdjustable: node.showValue,
      children: [],
    }

    this.groupNodesById.set(groupNode.id, groupNode)

    groupNode.children = children.map((child) =>
      this.buildNode(child, groupNode)
    )

    return groupNode
  }

  private calculateValues(): void {
    this.calculateGroupValues()
    this.calculateFormulaValues()
  }

  private calculateGroupValues(): void {
    this.rootNodes.forEach((node) => {
      if (node.type === 'Group') {
        this.calculateGroupValue(node)
      }
    })
  }

  private calculateGroupValue(group: GroupNode): number {
    const value = group.children.reduce((sum, child) => {
      if (child.type === 'Account') {
        return sum + child.adjustedValue
      }

      if (child.type === 'Group') {
        return sum + this.calculateGroupValue(child)
      }

      return sum
    }, 0)

    group.value = value
    group.adjustedDisplayValue = value * group.sign

    return value
  }

  private calculateFormulaValues(): void {
    const values: Record<string, number> = {}

    this.groupNodesById.forEach((group) => {
      if (group.identifier) {
        values[group.identifier] = group.adjustedDisplayValue
      }
    })

    const unresolvedFormulas = this.rootNodes.filter(
      (node) => node.type === 'Value'
    )

    const pendingFormulas = new Set(
      unresolvedFormulas
        .filter((node) => node.identifier)
        .map((node) => node.identifier as string)
    )

    let progress = true

    while (unresolvedFormulas.length > 0 && progress) {
      progress = false

      for (let i = unresolvedFormulas.length - 1; i >= 0; i -= 1) {
        const formulaNode = unresolvedFormulas[i]
        const result = new FormulaCalculator(
          formulaNode.formula,
          values,
          pendingFormulas
        ).calculate()

        if (result === null) {
          continue
        }

        formulaNode.adjustedValue = result

        if (formulaNode.identifier) {
          values[formulaNode.identifier] = result
          pendingFormulas.delete(formulaNode.identifier)
        }

        unresolvedFormulas.splice(i, 1)
        progress = true
      }
    }

    unresolvedFormulas.forEach((formulaNode) => {
      formulaNode.adjustedValue = null
    })
  }

  private storeInitialValues(): void {
    this.flatNodes().forEach((node) => {
      if (node.type === 'Group') {
        node.displayValue = node.adjustedDisplayValue
      }

      if (node.type === 'Value') {
        node.value = node.adjustedValue
      }
    })
  }

  private calculateAdjustmentPercentages(): void {
    this.flatNodes().forEach((node) => {
      if (node.type === 'Group') {
        node.adjustmentPercentage = this.groupAdjustmentPercentage(node)
      }

      if (node.type === 'Value') {
        node.adjustmentPercentage = this.formulaAdjustmentPercentage(node)
      }
    })
  }

  private groupAdjustmentPercentage(group: GroupNode): number | null {
    if (!group.showValue) return null
    if (group.adjustedDisplayValue === group.displayValue) return null
    if (group.displayValue === 0) return null
    return (group.adjustedDisplayValue / group.displayValue - 1) * 100
  }

  private formulaAdjustmentPercentage(formula: FormulaNode): number | null {
    if (formula.value === null || formula.adjustedValue === null) return null
    if (formula.adjustedValue === formula.value) return null
    if (formula.value === 0) return null
    return (formula.adjustedValue / formula.value - 1) * 100
  }

  applyAdjustment(
    node: AccountNode | GroupNode,
    newDisplayValue: number,
    percentage: number | null,
    expression: string | null
  ): void {
    if (node.type === 'Account') {
      node.adjustedDisplayValue = newDisplayValue
      node.adjustedValue = newDisplayValue * node.parent.sign
      node.adjustmentPercentage = percentage
      this.clearAncestorExpressions(node.parent)
    } else if (percentage !== null) {
      this.walkDescendants(node, (child) => {
        if (child.type === 'Account') {
          child.adjustedDisplayValue =
            child.displayValue * (1 + percentage / 100)
          child.adjustedValue = child.adjustedDisplayValue * child.parent.sign
          child.adjustmentPercentage = percentage
        }
        child.adjustmentExpression = null
      })
    } else {
      this.distributeEvenly(node, newDisplayValue)
    }

    node.adjustmentExpression = expression
    if (node.type === 'Group') {
      this.clearAncestorExpressions(node.parent)
    }

    this.calculateValues()

    if (node.type === 'Group') {
      // Override with the user's exact input to avoid float rounding from child sums
      node.adjustedDisplayValue = newDisplayValue
    }

    this.calculateAdjustmentPercentages()
  }

  resetAdjustment(node: AccountNode | GroupNode): void {
    if (node.type === 'Account') {
      node.adjustedValue = node.value
      node.adjustedDisplayValue = node.displayValue
      node.adjustmentPercentage = null
      node.adjustmentExpression = null
    } else {
      node.adjustmentExpression = null
      this.walkDescendants(node, (child) => {
        if (child.type === 'Account') {
          child.adjustedValue = child.value
          child.adjustedDisplayValue = child.displayValue
          child.adjustmentPercentage = null
        }
        child.adjustmentExpression = null
      })
    }

    this.calculateValues()
    this.calculateAdjustmentPercentages()
  }

  private distributeEvenly(group: GroupNode, newDisplayValue: number): void {
    const accounts: AccountNode[] = []

    this.walkDescendants(group, (child) => {
      if (child.type === 'Account') accounts.push(child)
      child.adjustmentExpression = null
    })

    if (accounts.length === 0) return

    const rawTotal = newDisplayValue * group.sign
    const valuePerAccount = rawTotal / accounts.length

    for (const account of accounts) {
      account.adjustedValue = valuePerAccount
      account.adjustedDisplayValue = valuePerAccount * account.parent.sign
      account.adjustmentPercentage = null
    }
  }

  private walkDescendants(
    group: GroupNode,
    fn: (child: AccountNode | GroupNode) => void
  ): void {
    for (const child of group.children) {
      if (child.type === 'Account') {
        fn(child)
      } else if (child.type === 'Group') {
        fn(child)
        this.walkDescendants(child, fn)
      }
    }
  }

  private clearAncestorExpressions(group: GroupNode | null): void {
    let current = group

    while (current) {
      current.adjustmentExpression = null
      current = current.parent
    }
  }

  flatNodes(): ForecastNode[] {
    return this.rootNodes.flatMap((node) => this.flattenNode(node))
  }

  private flattenNode(node: ForecastNode): ForecastNode[] {
    if (node.type === 'Group') {
      return [
        node,
        ...node.children.flatMap((child) => this.flattenNode(child)),
      ]
    }
    return [node]
  }

  getAccountById(accountId: number): AccountNode {
    return this.accountNodesById.get(accountId) as AccountNode
  }

  getGroupById(groupId: number): GroupNode {
    return this.groupNodesById.get(groupId) as GroupNode
  }

  // This method applies a small correction to the first account in a group
  // when the sum of the accounts doesn't match the group's total due to rounding
  getAccountEntriesForSave(): Array<{ accountId: number; value: number }> {
    const entries = new Map<number, number>()

    for (const [id, account] of this.accountNodesById) {
      entries.set(id, roundTo2(account.adjustedValue))
    }

    const groups = [...this.groupNodesById.values()]
      .filter((g) => g.showValue)
      .sort((a, b) => this.nodeDepth(b) - this.nodeDepth(a))

    for (const group of groups) {
      const expected = roundTo2(group.adjustedDisplayValue * group.sign)
      const descendantIds = this.descendantAccountIds(group)

      if (descendantIds.length === 0) continue

      const actual = descendantIds.reduce(
        (sum, id) => sum + entries.get(id)!,
        0
      )
      const diff = roundTo2(expected - actual)

      if (diff !== 0) {
        const firstId = descendantIds[0]
        entries.set(firstId, roundTo2(entries.get(firstId)! + diff))
      }
    }

    return [...entries].map(([accountId, value]) => ({ accountId, value }))
  }

  private nodeDepth(group: GroupNode): number {
    let depth = 0
    let current = group.parent

    while (current) {
      depth += 1
      current = current.parent
    }

    return depth
  }

  private descendantAccountIds(group: GroupNode): number[] {
    const ids: number[] = []

    const walk = (node: ForecastNode): void => {
      if (node.type === 'Account') {
        ids.push(node.id)
      } else if (node.type === 'Group') {
        node.children.forEach(walk)
      }
    }

    group.children.forEach(walk)
    return ids
  }

  // The nodes the user directly adjusted, each with the raw expression typed.
  // Persisted on save so the badges can be re-surfaced when the budget reopens.
  getAdjustmentsForSave(): LoadBudget.Adjustment[] {
    const adjustments: LoadBudget.Adjustment[] = []

    for (const node of this.flatNodes()) {
      if (node.type === 'Account' && node.adjustmentExpression !== null) {
        adjustments.push({
          accountId: node.id,
          reportGroupId: null,
          expression: node.adjustmentExpression,
        })
      } else if (node.type === 'Group' && node.adjustmentExpression !== null) {
        adjustments.push({
          accountId: null,
          reportGroupId: node.id,
          expression: node.adjustmentExpression,
        })
      }
    }

    return adjustments
  }

  // Re-surfaces a saved budget on top of the base ledger. Accounts the user did
  // not explicitly adjust arrive in budgetAccountValues as raw values (the only
  // place sign is applied: adjustedDisplayValue = raw * sign). Accounts the user
  // adjusted arrive in `adjustments` as display-value expressions and go through
  // the normal applyAdjustment path (which handles the sign), restoring their
  // value, badge and cell input. Group adjustments are prefill-only: their
  // descendants are the raw accounts above, so the group's value/badge roll up.
  loadBudget(
    budgetAccountValues: LoadBudget.BudgetAccountValue[],
    adjustments: LoadBudget.Adjustment[]
  ): void {
    for (const { accountId, value } of budgetAccountValues) {
      const account = this.accountNodesById.get(accountId)
      if (!account) continue

      const rawValue = Number.parseFloat(value)
      account.adjustedValue = rawValue
      account.adjustedDisplayValue = rawValue * account.parent.sign
    }

    this.calculateValues()
    this.calculateAdjustmentPercentages()

    for (const { accountId } of budgetAccountValues) {
      const account = this.accountNodesById.get(accountId)
      if (!account) continue

      account.adjustmentPercentage =
        account.displayValue === 0 ||
        account.adjustedDisplayValue === account.displayValue
          ? null
          : (account.adjustedDisplayValue / account.displayValue - 1) * 100
    }

    for (const { accountId, expression } of adjustments) {
      if (accountId === null) continue

      const account = this.accountNodesById.get(accountId)
      if (!account) continue

      const value = evaluateExpression(expression)
      if (value === null) continue

      const percentage =
        account.displayValue === 0
          ? null
          : (value / account.displayValue - 1) * 100

      this.applyAdjustment(account, value, percentage, expression)
    }

    for (const { reportGroupId, expression } of adjustments) {
      if (reportGroupId === null) continue

      const group = this.groupNodesById.get(reportGroupId)
      if (group) group.adjustmentExpression = expression
    }
  }

  hasAdjustments(): boolean {
    for (const account of this.accountNodesById.values()) {
      if (account.adjustedDisplayValue !== account.displayValue) return true
    }

    return false
  }

  formattedValue(node: ForecastNode): string {
    if (node.type === 'Group') {
      return node.showValue ? this.formatNumber(node.adjustedDisplayValue) : ''
    }

    if (node.type === 'Account') {
      return this.formatNumber(node.adjustedDisplayValue)
    }

    if (node.type === 'Value') {
      return node.adjustedValue === null
        ? '-'
        : this.formatNumber(node.adjustedValue)
    }

    if (node.type === 'Spacer') {
      return ''
    }

    return '-'
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat(this.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0) // Value can be -0 hence the `|| 0` to avoid displaying -0.00
  }
}

function roundTo2(value: number): number {
  return Math.round(value * 100) / 100
}
